'use client'

import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import type { ReactNode } from 'react'

/**
 * Link a un'ancora interna (`/#sezione`) che **garantisce** lo scorrimento.
 *
 * Perché non basta `<Link href="/#sezione">`: l'hash viene risolto una volta sola,
 * quando la navigazione si considera conclusa. La home è pesante e arriva in
 * streaming, quindi la sezione può non essere ancora nel DOM in quel momento: lo
 * scorrimento non avviene e nessuno lo ritenta. Qui l'elemento viene cercato a
 * ogni frame per qualche secondo, sia cambiando pagina sia restando su quella.
 */
export function HashScrollLink({
  href,
  className,
  onNavigate,
  children,
}: {
  href: string
  className?: string
  onNavigate?: () => void
  children: ReactNode
}) {
  const pathname = usePathname()
  const router = useRouter()
  const [base, hash] = href.split('#')
  // `/#sponsor` → base vuota, quindi la destinazione è la home.
  const target = base === '' ? '/' : base

  function scrollWhenReady() {
    // Fino a 10 s: con la home in streaming (e le sue query) l'attesa può essere
    // di secondi. `setTimeout` e non `requestAnimationFrame` per funzionare anche
    // quando la scheda non è in primo piano.
    const deadline = Date.now() + 10_000
    const tick = () => {
      const element = document.getElementById(hash)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return
      }
      if (Date.now() < deadline) window.setTimeout(tick, 80)
    }
    tick()
  }

  return (
    <Link
      href={href}
      className={className}
      onClick={(event) => {
        if (!hash) return
        event.preventDefault()
        onNavigate?.()
        // Lo scorrimento lo facciamo noi, quindi il router non deve provarci:
        // con `scroll: false` non si accavallano due tentativi.
        if (pathname === target) router.replace(href, { scroll: false })
        else router.push(href, { scroll: false })
        scrollWhenReady()
      }}
    >
      {children}
    </Link>
  )
}
